import api from './client'

export interface AutoAgentSession {
  session_id: string
  has_existing_state: boolean
  existing_state: Record<string, unknown>
}

export interface DeploymentProfile {
  id: number
  name: string
  db_type: string
  is_default?: boolean
}

export async function createAutoAgentSession(project: string): Promise<AutoAgentSession> {
  const { data } = await api.post('/autoagent/sessions', { project })
  return data
}

export async function clearAutoAgentState(sessionId: string): Promise<void> {
  await api.delete(`/autoagent/sessions/${sessionId}/state`)
}

export async function listDeploymentProfilesForAgent(): Promise<DeploymentProfile[]> {
  const { data } = await api.get('/autoagent/profiles')
  return data.profiles || []
}

export interface ActiveAgentSession {
  active: boolean
  session_id?: string
  project?: string
  status?: string
  has_running_task?: boolean
  // Highest event seq the live session has sent — a client that already has
  // the conversation attaches with ?after_seq= to get only what it missed.
  last_seq?: number
  active_plan_id?: string | null
}

export async function getActiveAgentSession(project?: string): Promise<ActiveAgentSession> {
  const params = project ? { project } : {}
  const { data } = await api.get('/autoagent/active', { params })
  return data
}

// One persisted conversation entry (see backend auto_agent_session.read_transcript).
export interface AutoAgentTranscriptEntry {
  sid: string
  seq: number
  ts: number
  plan_id?: string | null
  event: Record<string, unknown> & { type: string }
}

export interface AutoAgentTranscript {
  project: string
  entries: AutoAgentTranscriptEntry[]
  session_id: string | null
  last_seq: number
}

// The build conversation so far — shared by every device on the project, so
// a reload (or the phone picking the build up) shows what was said elsewhere.
export async function getAutoAgentTranscript(
  project: string, planId?: string | null, limit = 150,
): Promise<AutoAgentTranscript> {
  const params: Record<string, string | number> = { project, limit }
  if (planId) params.plan_id = planId
  const { data } = await api.get('/autoagent/transcript', { params })
  return data
}

export interface HandoffCode {
  code: string
  expires_in: number
  project: string
  plan_id: string | null
}

// A one-time code the twasta Build phone app redeems to log in as this user
// and open this project's build ("Continue on phone").
export async function createHandoffCode(project: string, planId?: string | null): Promise<HandoffCode> {
  const { data } = await api.post('/handoff/create', { project, plan_id: planId ?? null })
  return data
}
